
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Profile, UserRole } from "../types";
import { UserRoleSelect } from "./UserRoleSelect";
import { useToast } from "@/components/ui/use-toast";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { useUserRoles } from "@/hooks/useUserRoles";
import { Button } from "@/components/ui/button";
import { Bell } from "lucide-react";
import { UserProfileDialog } from "./UserProfileDialog";

interface UsersListProps {
  profiles: Profile[];
  isAdmin: boolean;
  onRoleChange: (userId: string, newRole: UserRole) => Promise<void>;
}

export const UsersList = ({ profiles, isAdmin, onRoleChange }: UsersListProps) => {
  const { toast } = useToast();
  const { user } = useSupabaseUser();
  const { isManager } = useUserRoles();
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  if (!profiles || profiles.length === 0) {
    return (
      <div className="text-center py-6 sm:py-8">
        <p className="text-gray-500 dark:text-gray-400">No users found</p>
      </div>
    );
  }

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    if (userId === user?.id) {
      toast({
        title: "Cannot modify your own role",
        description: "You cannot change your own role for security reasons",
        variant: "destructive",
      });
      return;
    }

    // Managers cannot change roles
    if (isManager && !isAdmin) {
      toast({
        title: "Permission denied",
        description: "Managers cannot change user roles",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setIsUpdating(prev => ({ ...prev, [userId]: true }));
      await onRoleChange(userId, newRole);
      
      toast({
        title: "Role updated",
        description: "User role has been updated successfully",
      });
    } catch (error: any) {
      console.error("Error updating role:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update user role",
        variant: "destructive",
      });
    } finally {
      setIsUpdating(prev => ({ ...prev, [userId]: false }));
    }
  };

  return (
    <>
      <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
        {profiles.map((user) => (
          <Card key={user.id} className="p-3 sm:p-4">
            <div className="space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium dark:text-white text-sm sm:text-base truncate">{user.full_name}</h3>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-300 truncate">{user.email}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 shrink-0"
                  onClick={() => {
                    setSelectedProfile(user);
                    setDialogOpen(true);
                  }}
                >
                  <Bell className="h-4 w-4" />
                </Button>
              </div>
              <UserRoleSelect
                defaultValue={user.user_roles[0]?.role || "guard"}
                onValueChange={(role) => handleRoleChange(user.id, role as UserRole)}
                disabled={!isAdmin || isUpdating[user.id] || (isManager && !isAdmin)}
              />
            </div>
          </Card>
        ))}
      </div>
      <UserProfileDialog
        profile={selectedProfile}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </>
  );
};
